import React, {useEffect, useState} from "react";
import {Box, makeStyles, Tab, Tabs, Typography,} from "@material-ui/core";
import {useForm} from "react-hook-form";
import apiHelper from "../../../../util/ApiHelper";
import {useLoading} from "../../../../util/LoadingProvider";
import EmailTemplateSubmissionForm from "./EmailTemplateSubmissionForm";
import {toast} from "react-toastify";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";

const EmailType = {
  CONFIRMATION_DIGITAL_CARD: 'confirmation-digital-card',
  CONFIRMATION_PHYSICAL_CARD: 'confirmation-physical-card',
  FAILURE: 'failure',
  RESET_PASSWORD: 'reset-password',
  IMPORT_CONFIRMATION: 'import-confirmation',
  RENEWAL_REMINDER: 'renewal-reminder',
  RENEWAL_CONFIRMATION_DIGITAL_CARD: 'renewal-confirmation-digital-card',
  RENEWAL_CONFIRMATION_PHYSICAL_CARD: 'renewal-confirmation-physical-card',
}

const emailTypes = Object.values(EmailType);

const EmailTypeDisplay = {
    [EmailType.CONFIRMATION_DIGITAL_CARD]: 'Confirmation (e-card)',
    [EmailType.CONFIRMATION_PHYSICAL_CARD]: 'Confirmation (physical card)',
    [EmailType.FAILURE]: 'Failure',
    [EmailType.RESET_PASSWORD]: 'Reset Password',
    [EmailType.IMPORT_CONFIRMATION]: 'Confirmation (Walk-in Members)',
    [EmailType.RENEWAL_REMINDER]: 'Renewal Reminder',
    [EmailType.RENEWAL_CONFIRMATION_DIGITAL_CARD]: 'Renewal Confirmation (e-card)',
    [EmailType.RENEWAL_CONFIRMATION_PHYSICAL_CARD]: 'Renewal Confirmation (physical card)',
}

const emailTabs = Object.values(EmailTypeDisplay)

const defaultFormTemplateFactory = () => ({
  id: null,
  subjectTC: '',
  contentTC: '',
  subjectEN: '',
  contentEN: '',
  type: '',
  createTime: ''
});

const defaultFormValues = (() => {
  const values = {};
  for (const key in EmailType) {
    values[EmailType[key]] = defaultFormTemplateFactory();
  }
  return values;
})()

const schemaFactory = () => (
  yup.object().shape({
    subjectEN: yup.string().required("Subject (En) is required"),
    subjectTC: yup.string().required("Subject (Tc) is required"),
  })
)

const schema = (() => {
    const schema = {};
    for (const key in EmailType) {
        schema[EmailType[key]] = schemaFactory();
    }
    return yup.object().shape(schema);
})()

const useStyles = makeStyles((theme) => ({
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: "20px 30px 10px",
    background: "#ffffff",
    minHeight: "800px",
  },
  tab: {
    fontSize: "13px",
  }
}));

function TabPanel(props) {
  const classes = useStyles();
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

export default function MemberEmailSettingsPage({
                                                  disabled,
                                                  ...props
                                                }) {
  const [defaultValues] = useState(defaultFormValues);
  const [initialValues, setInitialValues] = useState(defaultFormValues);
  const [activeTab, setActiveTab] = useState(0);
  const {setLoading} = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    reset
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    updateMemberEmailSettings(value);
  };

  const getMemberEmailTemplate = async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getMembershipEmailTemplate();
      const data = result.data;
      const dataObject = {};
      data.forEach((item) => {
        if (emailTypes.includes(item.type)) {
          dataObject[item.type] = item;
        }
      })
      setInitialValues(dataObject);
      reset(dataObject);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getMemberEmailTemplate();
  }, []);

  const updateMemberEmailSettings = async (data) => {
    try {
      setLoading(true);
      let promises = [];
      for (const key in data) {
        promises.push(apiHelper.hkiff.updateMembershipEmailTemplate(data[key]));
      }
      const results = await Promise.all(promises);
      if (results.every(item => item.data.code === 0)) {
        await getMemberEmailTemplate();
        toast.dark("Your changes is saved.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Member &gt; Email Setting
        </Typography>
      </Box>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{
          style: {
            top: "0",
            background: "#306F9D"
          }
        }}
      >
        {
            emailTabs.map((item, index) => (
                <Tab
                    label={item}
                    key={index}
                    className={classes.tab}
                    {...a11yProps(index)}
                />
            ))
        }
      </Tabs>
      {
        initialValues &&
        emailTypes.map((type, i) => (
            <TabPanel value={activeTab}
                      index={i}>
              <EmailTemplateSubmissionForm
                name={type}
                display={EmailTypeDisplay[type]}
                initialValues={initialValues}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                control={control}
                classes={classes}
                disabled={disabled}
              />
            </TabPanel>
          )
        )
      }
    </div>
  );
}
